import type { SocketDrop } from "../wire/protocol";

/**
 * Dials in a row that must fail to open before the panel names the network.
 *
 * One failed dial is a relay redeploy or a flaky moment, and pointing someone
 * at their antivirus for that sends them after nothing. Three spans the first
 * three backoffs, about 3.5 seconds.
 */
const BLAME_THE_NETWORK_AFTER = 3;

/**
 * The panel's line when registration failed, so no socket was ever dialled.
 *
 * ⚠️ This is the failure that leaves NOTHING in the relay's logs — no dial, no
 * drop, no report — so the panel is the only place it shows. The extension
 * hint is measured, not guessed: a user's panel failed this way with a browser
 * extension on and connected at once in an Incognito window. The raw error
 * still goes only to the console; see `BridgeStatus.error`.
 */
export const REGISTRATION_FAILED =
  "The app panel could not reach the MCP service. A browser extension such as an ad " +
  "blocker can cause this: try an Incognito window, which turns extensions off, to " +
  "check. Otherwise reopen the panel to try again.";

/**
 * The panel's status line for a socket that is not connected, or null while it is.
 *
 * ⚠️ The panel is the only place this can be seen. The person it happens to
 * is in their own browser, and nothing in `wrangler tail` records a socket
 * that never reached Cloudflare — so the line has to say which failure it is,
 * in words they can act on or read back to us.
 *
 * The refusal reason is rendered because it is OURS: `admit()` in
 * `relay/admission.ts` writes it and `refuse` bounds it to 100 characters.
 * That is the difference from `BridgeStatus.error`, whose content the host SDK
 * or the network chose and which goes to the console instead.
 */
export function describeDrop(drop: SocketDrop | null, relayUrl: string): string | null {
  if (drop === null) return null;
  switch (drop.kind) {
    case "refused":
      return drop.reason === ""
        ? "The relay refused this panel without saying why."
        : `The relay refused this panel: ${drop.reason}`;
    case "unreachable":
      if (drop.attempts < BLAME_THE_NETWORK_AFTER) {
        return `Could not open a connection to the relay. Retrying (attempt ${drop.attempts}).`;
      }
      return (
        `This browser could not open a connection to ${hostOf(relayUrl)} after ` +
        `${drop.attempts} attempts. Something is blocking it: most often a browser ` +
        "extension such as an ad blocker (an Incognito window turns extensions off, " +
        "so try one to check), or antivirus web scanning, a work proxy or a VPN. " +
        `Allow ${hostOf(relayUrl)} in it or pause it; the panel keeps retrying.`
      );
    case "dropped":
      return drop.code === null
        ? "The connection to the relay dropped (no close code). Reconnecting."
        : `The connection to the relay dropped (close code ${drop.code}). Reconnecting.`;
  }
}

/**
 * The panel's CONNECTION line: what is wrong with reaching the relay, or null.
 *
 * ⚠️ It has a line of its own because sharing one was how a failure went
 * unseen. The Connect flow writes the status line too, and pressing Connect —
 * the obvious thing to try when a panel will not connect — replaced a
 * registration failure with "This browser is linked", on the one failure that
 * leaves nothing in the relay's logs. This line is written by the bridge's
 * status alone, so nothing else can clear it.
 *
 * A registration failure wins over a drop: it means no socket was dialled this
 * time, so a drop left from an earlier dial is not the current story.
 *
 * Once the bridge has stopped dialling, the line says so. Otherwise a panel
 * that will never reconnect reads exactly like one waiting on its next try.
 */
export function problemLine(
  status: {
    readonly error: string | null;
    readonly drop: SocketDrop | null;
    readonly stopped: boolean;
  },
  relayUrl: string,
): string | null {
  if (status.error !== null) return REGISTRATION_FAILED;
  const line = describeDrop(status.drop, relayUrl);
  if (line === null || !status.stopped) return line;
  return `${line} Stopped trying after repeated refusals. Press Retry to try again.`;
}

/** The relay's host, which is what someone allow-listing it needs to type. */
function hostOf(relayUrl: string): string {
  try {
    return new URL(relayUrl).host;
  } catch {
    return "the relay";
  }
}
